import {
  Building,
  Calendar,
  CheckCircle2,
  CreditCard,
  Edit3,
  Mail,
  Save,
  Shield,
  User,
  XCircle,
} from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button, Card, Input } from '../components/ui';
import { useAuth } from '../contexts';
import { billingService } from '../services';
import { User as UserType } from '../services';
import { SubscriptionResponse, TokenPackage } from '../types';

const formatDate = (value?: string | null): string => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('pt-BR');
};

const getInitials = (firstName?: string, lastName?: string, fallback?: string): string => {
  const first = (firstName || '').trim();
  const last = (lastName || '').trim();
  if (first || last) {
    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase() || (fallback || '?').charAt(0).toUpperCase();
  }
  return (fallback || '?').charAt(0).toUpperCase();
};

const Profile: React.FC = () => {
  const { t } = useTranslation();
  const { user, updateUser, refreshUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [subscription, setSubscription] = useState<SubscriptionResponse | null>(null);
  const [subscriptionPackages, setSubscriptionPackages] = useState<TokenPackage[]>([]);
  const [isSubscriptionLoading, setIsSubscriptionLoading] = useState(false);
  const [subscriptionActionLoading, setSubscriptionActionLoading] = useState(false);

  useEffect(() => {
    if (!user) return;
    setFormData({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      email: user.email || '',
    });
  }, [user]);

  useEffect(() => {
    const loadSubscriptionData = async () => {
      setIsSubscriptionLoading(true);
      try {
        const [subscriptionResponse, packagesResponse] = await Promise.all([
          billingService.getSubscription().catch(() => null),
          billingService.getPackages(),
        ]);
        setSubscription(subscriptionResponse);
        setSubscriptionPackages(packagesResponse);
      } catch (error) {
        // Falhas no billing não devem quebrar a tela de perfil.
      } finally {
        setIsSubscriptionLoading(false);
      }
    };

    loadSubscriptionData();
  }, []);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const nextErrors: Record<string, string> = {};
    if (!formData.first_name.trim()) {
      nextErrors.first_name = t('profile.firstNameRequired');
    }
    if (!formData.last_name.trim()) {
      nextErrors.last_name = t('profile.lastNameRequired');
    }
    if (!formData.email.trim()) {
      nextErrors.email = t('profile.emailRequired');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      nextErrors.email = t('profile.emailInvalid');
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm() || !user) return;
    setIsLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 800));
      const updatedUser: UserType = {
        ...user,
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
      };
      updateUser(updatedUser);
      setIsEditing(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
      });
    }
    setErrors({});
    setIsEditing(false);
  };

  const getCurrencyFromPackage = (pkg: TokenPackage): string => {
    if (typeof pkg.price_brl === 'number') return 'BRL';
    if (typeof pkg.price_eur === 'number') return 'EUR';
    return 'USD';
  };

  const getAmountFromPackage = (pkg: TokenPackage): number => {
    if (typeof pkg.price_brl === 'number') return pkg.price_brl;
    if (typeof pkg.price_eur === 'number') return pkg.price_eur;
    return pkg.price_usd;
  };

  const formatPackagePrice = (pkg: TokenPackage): string => {
    const currency = getCurrencyFromPackage(pkg);
    const amount = getAmountFromPackage(pkg);
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const handleCancelSubscription = async () => {
    setSubscriptionActionLoading(true);
    try {
      await billingService.cancelSubscription({ cancel_at_period_end: true });
      const freshSubscription = await billingService.getSubscription();
      setSubscription(freshSubscription);
      refreshUser().catch(() => undefined);
    } finally {
      setSubscriptionActionLoading(false);
    }
  };

  const handleChangePlan = async (targetPackageId: number) => {
    setSubscriptionActionLoading(true);
    try {
      await billingService.changePlan({ target_package_id: targetPackageId });
      const freshSubscription = await billingService.getSubscription();
      setSubscription(freshSubscription);
      refreshUser().catch(() => undefined);
    } finally {
      setSubscriptionActionLoading(false);
    }
  };

  const fullName = useMemo(() => {
    if (!user) return '';
    return `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || '';
  }, [user]);

  if (!user) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <User className="h-12 w-12 mx-auto text-gray-400 mb-4" />
          <p className="text-gray-500">{t('profile.loadingProfile')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header com avatar + identidade + ações */}
      <Card>
        <div className="p-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-4">
            <div className="h-16 w-16 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 text-white flex items-center justify-center text-xl font-semibold shadow-sm">
              {getInitials(user.first_name, user.last_name, user.email)}
            </div>
            <div className="space-y-1">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {fullName || t('profile.title')}
              </h1>
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                <Mail className="h-4 w-4" />
                <span>{user.email}</span>
              </div>
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${
                    user.is_active
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200'
                      : 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200'
                  }`}
                >
                  {user.is_active ? (
                    <CheckCircle2 className="h-3.5 w-3.5" />
                  ) : (
                    <XCircle className="h-3.5 w-3.5" />
                  )}
                  {user.is_active ? t('profile.statusActive') : t('profile.statusInactive')}
                </span>
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${
                    user.is_verified
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200'
                      : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200'
                  }`}
                >
                  {user.is_verified ? (
                    <CheckCircle2 className="h-3.5 w-3.5" />
                  ) : (
                    <XCircle className="h-3.5 w-3.5" />
                  )}
                  {user.is_verified
                    ? t('profile.emailVerified')
                    : t('profile.emailNotVerified')}
                </span>
              </div>
            </div>
          </div>

          <Button
            onClick={() => (isEditing ? handleCancel() : setIsEditing(true))}
            variant={isEditing ? 'outline' : 'primary'}
          >
            <Edit3 className="h-4 w-4 mr-2" />
            {isEditing ? t('common.cancel') : t('profile.edit')}
          </Button>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Coluna esquerda: Informações Pessoais */}
        <Card>
          <div className="p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              {t('profile.personalInfo')}
            </h3>

            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('profile.firstName')}
                  </label>
                  {isEditing ? (
                    <Input
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleChange}
                      className={errors.first_name ? 'border-red-500' : ''}
                      placeholder={t('profile.firstNamePlaceholder')}
                    />
                  ) : (
                    <p className="text-gray-900 dark:text-white py-2">
                      {user.first_name || t('profile.notProvided')}
                    </p>
                  )}
                  {errors.first_name && (
                    <p className="mt-1 text-sm text-red-600">{errors.first_name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('profile.lastName')}
                  </label>
                  {isEditing ? (
                    <Input
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleChange}
                      className={errors.last_name ? 'border-red-500' : ''}
                      placeholder={t('profile.lastNamePlaceholder')}
                    />
                  ) : (
                    <p className="text-gray-900 dark:text-white py-2">
                      {user.last_name || t('profile.notProvided')}
                    </p>
                  )}
                  {errors.last_name && (
                    <p className="mt-1 text-sm text-red-600">{errors.last_name}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('profile.email')}
                </label>
                {isEditing ? (
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={errors.email ? 'border-red-500' : ''}
                    placeholder={t('profile.emailPlaceholder')}
                  />
                ) : (
                  <p className="text-gray-900 dark:text-white py-2">{user.email}</p>
                )}
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                )}
              </div>
            </div>

            {isEditing && (
              <div className="flex justify-end space-x-3 mt-6">
                <Button onClick={handleCancel} variant="outline">
                  {t('common.cancel')}
                </Button>
                <Button onClick={handleSave} loading={isLoading}>
                  <Save className="h-4 w-4 mr-2" />
                  {t('common.save')}
                </Button>
              </div>
            )}
          </div>
        </Card>

        {/* Coluna direita: Plano + Organização & Conta */}
        <div className="space-y-6">
          <Card>
            <div className="p-6">
              <div className="flex items-start justify-between gap-2 mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  {t('subscription.summaryTitle')}
                </h3>
                <Link to="/subscription">
                  <Button variant="outline" size="sm">
                    <CreditCard className="h-4 w-4 mr-2" />
                    {t('subscription.manage')}
                  </Button>
                </Link>
              </div>

              {isSubscriptionLoading ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('common.loading')}
                </p>
              ) : !subscription?.has_subscription ? (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t('subscription.noActivePlan')}
                  </p>
                  <Link to="/subscription">
                    <Button size="sm">{t('subscription.choosePlan')}</Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {t('subscription.currentPlan')}
                    </span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {subscription.package_name || '-'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {t('subscription.status')}
                    </span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {subscription.status || '-'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {t('subscription.nextRenewal')}
                    </span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {formatDate(subscription.current_period_end)}
                    </span>
                  </div>

                  <div className="pt-2 border-t border-gray-200 dark:border-gray-700 space-y-2">
                    {subscriptionPackages
                      .filter(pkg => pkg.id !== subscription.package_id)
                      .slice(0, 2)
                      .map(pkg => (
                        <Button
                          key={pkg.id}
                          variant="outline"
                          size="sm"
                          className="w-full justify-between"
                          onClick={() => handleChangePlan(pkg.id)}
                          loading={subscriptionActionLoading}
                        >
                          <span>{pkg.name}</span>
                          <span>{formatPackagePrice(pkg)}</span>
                        </Button>
                      ))}
                    <Button
                      variant="error"
                      size="sm"
                      className="w-full"
                      onClick={handleCancelSubscription}
                      loading={subscriptionActionLoading}
                    >
                      {t('subscription.cancelPlan')}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </Card>

          <Card>
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                {t('profile.organizationAndAccount')}
              </h3>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Building className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {user.organization?.name || t('profile.notProvided')}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {user.organization?.description || t('profile.organizationNoDescription')}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Calendar className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {t('profile.memberSince')}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {formatDate(user.created_at)}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Shield className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {t('profile.userId')}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                      #{user.id}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Profile;
